import { ReactElement, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Crud, CrudMode, notification, transformCrudFormErrors } from '@ohpen/enso-ui';
import type { CrudFormComponent, CrudProps, ValidationError } from '@ohpen/enso-ui';
import { ActionType, lendersCreateChangeRequest } from '@pmp/api';
import { changeRequestNotificationMessage } from '@pmp/utils';
import { useFeatureToggle } from '@pmp/hooks';

import { getComplianceSchema } from '../complianceFormConfig';
import type { ComplianceCrudFormProps, ComplianceFormValues } from '../interfaces';
import { toComplianceFormValues, toLenderDraftDto } from '../utils';

export const ComplianceCrudForm = ({ details, onClose, refresh }: ComplianceCrudFormProps): ReactElement => {
  const ref = useRef<CrudFormComponent>(null);
  const [open, setOpen] = useState(true);
  const { t } = useTranslation();
  const enableFourEyes = useFeatureToggle(['enableFourEyes']);

  const handleOnClose = useCallback(() => {
    setOpen(false);
    setTimeout(onClose, 300);
  }, [onClose]);

  const onConfirm = useCallback(
    (values: ComplianceFormValues): Promise<ValidationError[] | void> => {
      const data = toLenderDraftDto(details, values);
      return lendersCreateChangeRequest({ body: { data, actionType: ActionType.EDIT } }).then(({ data: responseData }) => {
        if (!responseData.isValid) {
          // The lock-conflict error lands on "id", which has no form field to attach to - show it
          // as a notification instead of silently dropping it via transformCrudFormErrors.
          const { id: lockErrors, ...fieldErrors } = responseData.errors ?? {};
          if (lockErrors?.length) notification().error({ title: lockErrors.join('\n') });
          return transformCrudFormErrors(fieldErrors as Record<string, string[]>);
        }
        refresh();
        notification().success({ title: changeRequestNotificationMessage(t, enableFourEyes) });
      });
    },
    [details, refresh, t, enableFourEyes],
  );

  const crudProps: CrudProps<ComplianceFormValues> = useMemo(
    () => ({
      ref,
      open,
      schema: getComplianceSchema(t),
      title: t('lender.lender.labels.editComplianceConfiguration'),
      labels: { confirm: t('global.labels.save') },
      width: 1100,
      initialValues: toComplianceFormValues(details.complianceCheckConfiguration),
      mode: CrudMode.EDIT,
      onClose: handleOnClose,
      onConfirm,
    }),
    [open, details, t, handleOnClose, onConfirm],
  );

  return <Crud {...crudProps} />;
};
